// src/hooks/useSystemMuteNotification/useSystemMuteNotification.ts
import { useState, useCallback } from 'react';
import { DeviceState } from '../useSmartDeviceDetection/useSmartDeviceDetection';

interface SystemMuteState {
  showAudioToast: boolean;
  showVideoToast: boolean;
  dismissedToasts: Set<string>;
}

export function useSystemMuteNotification() {
  const [state, setState] = useState<SystemMuteState>({
    showAudioToast: false,
    showVideoToast: false,
    dismissedToasts: new Set(),
  });

  // Show toast for system-level muting
  const showSystemMuteToast = useCallback(
    (deviceType: 'microphone' | 'camera', error: any) => {
      // Only show for system-level blocks
      if (error?.state !== DeviceState.SYSTEM_BLOCKED) {
        return;
      }

      // Don't show if already dismissed recently (within session)
      const toastKey = `${deviceType}_system_mute`;
      if (state.dismissedToasts.has(toastKey)) {
        return;
      }

      setState(prev => ({
        ...prev,
        [deviceType === 'microphone' ? 'showAudioToast' : 'showVideoToast']: true,
      }));
    },
    [state.dismissedToasts]
  );

  // Hide toast
  const hideSystemMuteToast = useCallback((deviceType: 'microphone' | 'camera') => {
    const toastKey = `${deviceType}_system_mute`;

    setState(prev => ({
      ...prev,
      [deviceType === 'microphone' ? 'showAudioToast' : 'showVideoToast']: false,
      dismissedToasts: new Set([...prev.dismissedToasts, toastKey]),
    }));
  }, []);

  // Reset dismissed toasts (e.g., when user fixes the issue)
  const resetDismissedToasts = useCallback(() => {
    setState(prev => ({
      ...prev,
      dismissedToasts: new Set(),
    }));
  }, []);

  return {
    showAudioToast: state.showAudioToast,
    showVideoToast: state.showVideoToast,
    showSystemMuteToast,
    hideSystemMuteToast,
    resetDismissedToasts,
  };
}
